import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, ReplaySubject, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { appConfig, clientConfig } from "../shared/constant/app.constant";
import { TranslationLevel, TranslationRequest } from "../shared/models/translation-request.model";

@Injectable({
  providedIn: 'root',
})
export class CustomTranslateLoader implements TranslateLoader {
  public translations: { [key: string]: any } = {};
  public translationSubject: { [key: string]: ReplaySubject<any> } = {};

  private _url = `${appConfig.BASE_URL}ReturnPortalFacade`;

  constructor(private http: HttpClient) {}

  // gets called internally from ngx-translate pkg when selected language translation is not loaded or failed at application start for any reason
  getTranslation(lang: string): Observable<any> {

    // If requested language is not supported in backend, try to fallback to default language, otherwise use lang as is
    const supportedLanguages: string[] = clientConfig?.LANG?.list || [];
    const defaultLang = clientConfig?.LANG?.default;

    const langLower = lang?.toLowerCase();
    const isSupported = langLower && supportedLanguages.some(sl => sl?.toLowerCase() === langLower);
    if (!isSupported && defaultLang) {
      const defaultLangLower = defaultLang.toLowerCase();
      const isDefaultSupported = supportedLanguages.some(sl => sl?.toLowerCase() === defaultLangLower);
      if (isDefaultSupported) {
        lang = defaultLang;
      }
    }

    let translationRequest: TranslationRequest = {
      translationLevel: TranslationLevel.MERCHANT,
      merchantId: clientConfig.MERCHANT_ID,
      languages: [lang],
      includeTenantIfMerchantEmpty: true
    };

    return this.http.post(`${this._url}/GetTranslations`, translationRequest).pipe(
      map((response: any) => {
        if (response && response.translations) {
          return response.translations[0];
        }
        return {};
      }),
      catchError(() => {
        // Return an empty object if the language is not supported
        return of({});
      })
    );
  }

  /*  gets called from i18n-module in APP_INITIALIZER to load all supported language translations at once from API at application start,
      so that we don't get UI components loaded before loading translations. Because of use of APP_INITIALIZER we are loading translations when app loader is shown.
      So, when the language is changed we have translations for all languages.
      In case any language translation loading fails, and when we switch to that language through UI,
      above getTranslation method is called internally if and only if the translation is not present  */
  getAllTranslations(languages: string[]): Observable<any> {
    let translationRequest: TranslationRequest = {
      translationLevel: TranslationLevel.MERCHANT,
      merchantId: clientConfig.MERCHANT_ID,
      languages: languages,
      includeTenantIfMerchantEmpty: true
    };

    return this.http.post(`${this._url}/GetTranslations`, translationRequest).pipe(
      map((response: any) => {
        if (response && response.translations) {
          let translationData = {};
          for (const translation of response.translations) {
            translationData[translation.language] = translation;
          }
          return translationData;
        }
        return {};
      }),
      catchError(() => {
        // Return an empty object if the translations are not present
        return of({});
      })
    );
  }
}
